/**
 * Professional Vendor Card Component
 * Displays vendor/supplier information across all marketplaces
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  Box,
  Rating,
  Chip,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Verified,
  LocationOn,
  Phone,
  Email,
  Language,
  Star,
  Business,
  Schedule,
  LocalShipping,
  Security,
  WhatsApp,
  Message,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatPhoneNumber } from '../../../utils/helpers';

const VendorCard = ({ 
  vendor, 
  category,
  showActions = true,
  onContact,
  onAddToFavorites,
  isFavorite = false,
  layout = 'card' // 'card' | 'compact' | 'detailed'
}) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/marketplace/${category}/vendors/${vendor.id}`);
  };

  const handleContact = (method) => {
    if (onContact) {
      onContact(vendor, method);
    }
  };

  const getVerificationBadges = () => {
    const badges = [];
    if (vendor.isVerified) badges.push({ label: 'Verified', color: 'success', icon: <Verified /> });
    if (vendor.isRegistered) badges.push({ label: 'Registered', color: 'info', icon: <Business /> });
    if (vendor.hasPremium) badges.push({ label: 'Premium', color: 'warning', icon: <Star /> });
    if (vendor.hasInsurance) badges.push({ label: 'Insured', color: 'success', icon: <Security /> });
    return badges;
  };

  const getSpecializations = () => {
    return vendor.specializations || vendor.categories || [];
  };

  if (layout === 'compact') {
    return (
      <Card sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1 }}>
        <Avatar
          src={vendor.logo}
          alt={vendor.name}
          sx={{ width: 48, height: 48, mr: 2 }}
        >
          {vendor.name?.[0]}
        </Avatar>
        
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {vendor.name}
            </Typography>
            {vendor.isVerified && <Verified color="primary" sx={{ fontSize: 16 }} />}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Rating value={vendor.rating || 0} size="small" readOnly />
            <Typography variant="caption" color="text.secondary">
              {vendor.reviewCount || 0} reviews
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {vendor.location}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          onClick={handleViewProfile}
        >
          View Profile
        </Button>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* Header with Avatar and Basic Info */}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              vendor.isOnline ? (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    border: '2px solid white'
                  }}
                />
              ) : null
            }
          >
            <Avatar
              src={vendor.logo}
              alt={vendor.name}
              sx={{ 
                width: 64, 
                height: 64,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              {vendor.name?.[0]}
            </Avatar>
          </Badge>

          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {vendor.name}
              </Typography>
              {showActions && (
                <IconButton
                  size="small"
                  onClick={() => onAddToFavorites && onAddToFavorites(vendor)}
                >
                  {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                </IconButton>
              )}
            </Box>

            {/* Rating and Reviews */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={vendor.rating || 0} precision={0.1} size="small" readOnly />
              <Typography variant="body2" color="text.secondary">
                {vendor.rating?.toFixed(1)} ({vendor.reviewCount || 0} reviews)
              </Typography>
            </Box>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {vendor.location}
              </Typography>
              {vendor.deliveryRadius && (
                <Chip
                  label={`${vendor.deliveryRadius}km radius`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Verification Badges */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {getVerificationBadges().map((badge, index) => (
            <Chip
              key={index}
              icon={badge.icon}
              label={badge.label}
              size="small"
              color={badge.color}
              variant="outlined"
            />
          ))}
        </Box>

        {/* Description */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {vendor.description}
        </Typography>

        {/* Specializations */}
        {getSpecializations().length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Specializations:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {getSpecializations().slice(0, 3).map((spec, index) => (
                <Chip key={index} label={spec} size="small" variant="outlined" />
              ))}
              {getSpecializations().length > 3 && (
                <Chip 
                  label={`+${getSpecializations().length - 3}`} 
                  size="small" 
                  variant="outlined" 
                />
              )}
            </Box>
          </Box>
        )}

        {/* Key Stats */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {vendor.projectsCompleted || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Projects
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {vendor.yearsExperience || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Years Exp
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {vendor.responseTime || '2h'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Response
            </Typography>
          </Box>
        </Box>

        {/* Services/Features */}
        <List dense sx={{ py: 0 }}>
          {vendor.freeDelivery && (
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <LocalShipping sx={{ fontSize: 18, color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Free Delivery" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          )}
          
          {vendor.is24x7Available && (
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Schedule sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="24/7 Available" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          )}

          {vendor.minOrderAmount && (
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Business sx={{ fontSize: 18, color: 'warning.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={`Min Order: ${formatCurrency(vendor.minOrderAmount)}`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          )}
        </List>
      </CardContent>

      <Divider />

      {/* Actions */}
      {showActions && (
        <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {vendor.whatsappNumber && (
              <Tooltip title="WhatsApp">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleContact('whatsapp')}
                >
                  <WhatsApp />
                </IconButton>
              </Tooltip>
            )}

            {vendor.phone && (
              <Tooltip title="Call">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleContact('call')}
                >
                  <Phone />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Message">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleContact('message')}
              >
                <Message />
              </IconButton>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={handleViewProfile}
            sx={{ minWidth: 100 }}
          >
            View Profile
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export default VendorCard;